import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Users, Plus, Copy, Trash2, QrCode, Mail } from 'lucide-react'
import { sessionAPI } from '../services/api'
import type { Session } from '../types'
import toast from 'react-hot-toast'
import QRCodeGenerator from '../components/QRCodeGenerator'

const Sessions: React.FC = () => {
  const { t } = useTranslation()
  const [sessions, setSessions] = useState<Session[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newSessionName, setNewSessionName] = useState('')
  const [newSessionDescription, setNewSessionDescription] = useState('')
  const [newSessionCode, setNewSessionCode] = useState('')
  const [showQRCode, setShowQRCode] = useState<number | null>(null)
  const [showEmailModal, setShowEmailModal] = useState<number | null>(null)
  const [emailAddress, setEmailAddress] = useState('')
  const [isSendingEmail, setIsSendingEmail] = useState(false)

  useEffect(() => {
    fetchSessions()
  }, [])

  const fetchSessions = async () => {
    try {
      const response = await sessionAPI.getMySessions()
      setSessions(response.data)
    } catch (error) {
      console.error('Error fetching sessions:', error)
      // API nie ma tego endpointu, używamy pustej listy
      setSessions([])
    } finally {
      setIsLoading(false)
    }
  }

  const validateSessionCode = (code: string): boolean => {
    if (!code) return true // Empty is allowed (auto-generate)
    const regex = /^[A-Z0-9]{4,20}$/
    return regex.test(code)
  }

  const handleCreateSession = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newSessionName.trim()) return

    // Validate custom code
    if (newSessionCode && !validateSessionCode(newSessionCode)) {
      toast.error(t('sessions.codeInvalid'))
      return
    }

    try {
      await sessionAPI.createSession({ 
        name: newSessionName, 
        description: newSessionDescription,
        session_code: newSessionCode.trim().toUpperCase() || undefined
      })
      toast.success(t('sessions.createSuccess'))
      setNewSessionName('')
      setNewSessionDescription('')
      setNewSessionCode('')
      setShowCreateForm(false)
      fetchSessions()
    } catch (error: any) {
      // Check if it's a duplicate code error
      if (error?.response?.status === 409 || error?.response?.data?.error?.includes('already exists')) {
        toast.error(t('sessions.codeExists'))
      } else {
        toast.error(t('sessions.createError'))
      }
    }
  }

  const handleDeleteSession = async (sessionId: number) => {
    if (!confirm(t('sessions.confirmDelete'))) return

    try {
      await sessionAPI.deleteSession(sessionId)
      toast.success(t('sessions.deleteSuccess'))
      fetchSessions()
    } catch (error) {
      toast.error(t('sessions.deleteError'))
    }
  }

  const copySessionCode = (code: string) => {
    navigator.clipboard.writeText(code)
    toast.success(t('sessions.codeCopied'))
  }

  const generateJoinURL = (sessionCode: string) => {
    // URL do aplikacji webowej do dołączenia do sesji 
    return `https://app.presentclic.com/?code=${sessionCode}`
  }

  const handleSendEmail = async (sessionId: number) => {
    if (!emailAddress || !emailAddress.trim()) {
      toast.error(t('error.emailRequired'))
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(emailAddress)) {
      toast.error(t('error.invalidEmail'))
      return
    }

    setIsSendingEmail(true)
    try {
      await sessionAPI.sendSessionEmail(sessionId, emailAddress)
      toast.success(t('sessions.emailSentSuccess'))
      setShowEmailModal(null)
      setEmailAddress('')
    } catch (error: any) {
      console.error('Error sending email:', error)
      toast.error(t('sessions.emailSentError'))
    } finally {
      setIsSendingEmail(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">{t('sessions.title')}</h1>
          <p className="text-gray-400">{t('sessions.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          <span>{t('sessions.createNew')}</span>
        </button>
      </div>

      {/* Create Session Form */}
      {showCreateForm && (
        <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
          <h3 className="text-lg font-semibold text-white mb-4">{t('sessions.createNew')}</h3>
          <form onSubmit={handleCreateSession} className="space-y-4">
            <div>
              <input
                type="text"
                value={newSessionName}
                onChange={(e) => setNewSessionName(e.target.value)}
                placeholder={t('sessions.sessionName')}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>
            <div>
              <textarea
                value={newSessionDescription}
                onChange={(e) => setNewSessionDescription(e.target.value)}
                placeholder={t('sessions.descriptionPlaceholder')}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
                rows={3}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('sessions.sessionCode')} ({t('common.optional')})
              </label>
              <input
                type="text"
                value={newSessionCode}
                onChange={(e) => setNewSessionCode(e.target.value.toUpperCase())}
                placeholder={t('sessions.codePlaceholder')}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
                maxLength={20}
                pattern="[A-Z0-9]*"
              />
              <p className="text-xs text-gray-500 mt-1">{t('sessions.codeHelp')}</p>
            </div>
            <div className="flex space-x-4">
              <button
                type="submit"
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
              >
                {t('sessions.create')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false)
                  setNewSessionName('')
                  setNewSessionDescription('')
                  setNewSessionCode('')
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500"
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Sessions List */}
      <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
          <Users className="h-5 w-5 text-primary mr-2" />
          {t('sessions.yourSessions')}
        </h2>

        {isLoading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse bg-dark-700 h-20 rounded"></div>
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">{t('sessions.noSessions')}</p>
            <p className="text-sm text-gray-500 mt-2">
              {t('sessions.noSessionsDescription')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-dark-700">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('sessions.name')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('sessions.code')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('sessions.participants')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('sessions.created')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('common.status')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session) => (
                  <tr key={session.id} className="border-b border-dark-700 hover:bg-dark-700/50">
                    <td className="py-4 px-4">
                      <p className="font-medium text-white">{session.name}</p>
                      {session.description && (
                        <p className="text-sm text-gray-400 mt-1">{session.description}</p>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        <span className="font-mono text-primary bg-primary/10 px-2 py-1 rounded">
                          {session.session_code}
                        </span>
                        <button
                          onClick={() => copySessionCode(session.session_code)}
                          className="p-1 text-gray-400 hover:text-white"
                          title="Skopiuj kod"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-gray-300">
                        {session.connected_devices || 0} / {session.device_limit || 50}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-gray-300">
                        {new Date(session.created_at).toLocaleDateString()}
                      </p>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${
                        session.is_active 
                          ? 'bg-green-900/20 text-green-400 border-green-900/50' 
                          : 'bg-gray-900/20 text-gray-400 border-gray-900/50'
                      }`}>
                        {session.is_active ? t('sessions.active') : t('sessions.inactive')}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setShowQRCode(session.id)}
                          className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-600/10 rounded-lg transition-colors"
                          title={t('sessions.showQRCode')}
                        >
                          <QrCode className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setShowEmailModal(session.id)}
                          className="p-2 text-green-400 hover:text-green-300 hover:bg-green-600/10 rounded-lg transition-colors"
                          title={t('sessions.sendEmail')}
                        >
                          <Mail className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteSession(session.id)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-600/10 rounded-lg transition-colors"
                          title={t('common.delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* QR Code Modal */}
      {showQRCode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-dark-800 rounded-lg p-6 border border-dark-700 max-w-md w-full mx-4">
            {(() => {
              const session = sessions.find(s => s.id === showQRCode)
              if (!session) return null
              
              return (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-white">{t('sessions.qrCodeForSession')}</h3>
                    <button
                      onClick={() => setShowQRCode(null)}
                      className="text-gray-400 hover:text-white"
                    >
                      ✕
                    </button>
                  </div>
                  
                  <div className="text-center space-y-4">
                    <div className="bg-white p-4 rounded-lg inline-block">
                      <QRCodeGenerator 
                        value={generateJoinURL(session.session_code)}
                        size={200}
                      />
                    </div>
                    
                    <div>
                      <p className="text-gray-300 mb-2">{t('sessions.sessionName')}:</p>
                      <p className="font-semibold text-white">{session.name}</p>
                    </div>
                    
                    <div>
                      <p className="text-gray-300 mb-2">{t('sessions.code')}:</p>
                      <p className="font-mono text-primary text-lg">{session.session_code}</p>
                    </div>
                    
                    <div>
                      <p className="text-gray-300 mb-2">{t('sessions.joinLink')}:</p>
                      <p className="text-sm text-blue-400 break-all">
                        {generateJoinURL(session.session_code)}
                      </p>
                    </div>
                    
                    <div className="flex space-x-2 mt-6">
                      <button
                        onClick={() => copySessionCode(session.session_code)}
                        className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                      >
                        {t('sessions.copy')} {t('sessions.code')}
                      </button>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(generateJoinURL(session.session_code))
                          toast.success(t('sessions.linkCopied'))
                        }}
                        className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500"
                      >
                        {t('sessions.copy')} {t('sessions.joinLink')}
                      </button>
                    </div>
                  </div>
                </>
              )
            })()}
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-dark-800 rounded-lg p-6 border border-dark-700 max-w-md w-full mx-4">
            {(() => {
              const session = sessions.find(s => s.id === showEmailModal)
              if (!session) return null
              
              return (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-white">{t('sessions.sendEmailTitle')}</h3>
                    <button
                      onClick={() => {
                        setShowEmailModal(null)
                        setEmailAddress('')
                      }}
                      className="text-gray-400 hover:text-white"
                    >
                      ✕
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <p className="text-gray-300 mb-2">{t('sessions.session')}:</p>
                      <p className="font-semibold text-white">{session.name}</p>
                      <p className="text-sm text-gray-400">{t('sessions.code')}: {session.session_code}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        {t('sessions.recipientEmail')}:
                      </label>
                      <input
                        type="email"
                        value={emailAddress}
                        onChange={(e) => setEmailAddress(e.target.value)}
                        placeholder={t('sessions.emailPlaceholder')}
                        className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
                        disabled={isSendingEmail}
                      />
                    </div>
                    
                    <div className="text-sm text-gray-400">
                      <p>{t('sessions.emailWillContain')}</p>
                      <ul className="list-disc list-inside mt-1">
                        <li>{t('sessions.qrCodeToScan')}</li>
                        <li>{t('sessions.sessionCodeManual')}</li>
                        <li>{t('sessions.directLink')}</li>
                      </ul>
                    </div>
                    
                    <div className="flex space-x-2 mt-6">
                      <button
                        onClick={() => handleSendEmail(session.id)}
                        disabled={isSendingEmail || !emailAddress}
                        className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSendingEmail ? t('sessions.sending') : t('sessions.send')}
                      </button>
                      <button
                        onClick={() => {
                          setShowEmailModal(null)
                          setEmailAddress('')
                        }}
                        disabled={isSendingEmail}
                        className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500"
                      >
                        {t('sessions.cancel')}
                      </button>
                    </div>
                  </div>
                </>
              )
            })()}
          </div>
        </div>
      )}
    </div>
  )
}

export default Sessions